import React, { useMemo } from "react";
import _ from "lodash";
import moment from "moment/moment";

import { TRANSACTION_INSTR, TRANSACTION_TYPE_VALUES, nextAvailableDate } from "../../../../../constants";
import {
  getPortfolioComponentsWithSubDepots,
  instrCustodianData,
  setInstrumentBankData,
  validatePortfolio,
} from "../../../../../utils";
import { instrumentToStateFormat } from "../../../../../../../components/TradingStore/utils";
import { extendPaymentPlanWithInstrumentData } from "../../../../../../CustomerDashboard/utils";

const paymentPlanToTransactionData = (paymentPlan, action) => ({
  transaction_value: paymentPlan.rate && Math.abs(parseFloat(paymentPlan.rate)),
  rotation: paymentPlan.shifts_value,
  from_date:
    paymentPlan.start_date &&
    moment(paymentPlan.start_date, "YYYY-MM-DD").toDate(),
  till_date:
    action !== "delete" &&
    paymentPlan.end_date &&
    moment(paymentPlan.end_date, "YYYY-MM-DD").toDate(),
  transaction_type: TRANSACTION_INSTR.amount,
  discount: paymentPlan.discount,
});

/**
 * If the item's from_date is in the past or not available, sets it to the next
 * available execution date. Mutates the item.
 */
export const setNextExecutionDateIfNeeded = (item, portfolio, transactionType) => {
  const fromDate = item.from_date && moment(item.from_date);
  const nextDate = nextAvailableDate(fromDate, portfolio, transactionType);
  if (nextDate && (!fromDate || nextDate.format() !== fromDate.format())) {
    item.from_date = nextDate;
  }
};

export default function usePaymentPlanTrade({
  portfolio,
  handlePortfolioChange,
  paymentPlanTransactionKey,
  updateDefaults,
}) {
  const planConfig = {
    [TRANSACTION_TYPE_VALUES.SAVINGS_PLAN]: {
      tillDateEnabledKey: "ordering_savings_plan_till_date_enabled",
      rotationOptionsKey: portfolio.isModelportfolio
        ? "availableMPSavingsPlanPeriodicPlans"
        : "availableSavingsPlanPeriodicPlans",
    },
    [TRANSACTION_TYPE_VALUES.PAYOUT_PLAN]: {
      tillDateEnabledKey: "ordering_payout_plan_till_date_enabled",
      rotationOptionsKey: portfolio.isModelportfolio
        ? "availableMPPayoutPlanPeriodicPlans"
        : "availablePayoutPlanPeriodicPlans",
    },
    [TRANSACTION_TYPE_VALUES.SWITCH_PLAN]: {
      tillDateEnabledKey: "ordering_switch_plan_till_date_enabled",
      rotationOptionsKey: portfolio.isModelportfolio
        ? "availableMPSwitchPlanPeriodicPlans"
        : "availableSwitchPlanPeriodicPlans",
    },
  }[paymentPlanTransactionKey];

  const tillDateEnabled = portfolio[planConfig.tillDateEnabledKey];
  const rotationOptions = portfolio[planConfig.rotationOptionsKey] || [];

  const initPlan = (instr) => {
    if (!tillDateEnabled) {
      instr.till_date = undefined;
    }

    if (!instr.from_date){
      setNextExecutionDateIfNeeded(instr, portfolio, paymentPlanTransactionKey)
    }

    if(!instr.rotation){
      instr.rotation = rotationOptions[0].value;
    }

    // Resolve rotation label from options if not already set
    if (instr.rotation && !instr.rotationLabel) {
      instr.rotationLabel = getRotationLabel(instr.rotation);
    }
  }

  const updatePlanDefaults = React.useCallback(
    (data) => updateDefaults(data, paymentPlanTransactionKey),
    [updateDefaults]
  )

  const getRotationLabel = (rotationValue) => {
    const option = rotationOptions.find((o) => o.value === rotationValue);
    return option ? option.label : rotationValue;
  };

  const transactions = portfolio.transactions[paymentPlanTransactionKey] || [];

  const groupedTransactions = useMemo(() => {
    const groups = { create: [], edit: [], delete: [] };

    transactions.forEach((instr) => {
      const savingsPlanData = instr.data || {};
      _.defaults(instr, paymentPlanToTransactionData(savingsPlanData, instr.action));
      initPlan(instr);

      if (paymentPlanTransactionKey === TRANSACTION_TYPE_VALUES.SWITCH_PLAN) {
        _.defaults(instr, {
          buy: (savingsPlanData.components || []).map((instrument) => {
            let instrumentCopy = _.cloneDeep(instrument);
            if (!instrumentCopy.instrumentId) {
              instrumentCopy = instrumentToStateFormat(
                instrumentCopy,
                undefined,
                { action: savingsPlanData.action },
                "payment_id"
              );
            }
            _.defaults(
              instrumentCopy,
              paymentPlanToTransactionData(instrumentCopy.data || {}, instr.action)
            );
            initPlan(instrumentCopy);
            setInstrumentBankData(
              instrumentCopy,
              instrCustodianData(instrumentCopy.data, portfolio.companyId),
              true
            );
            return instrumentCopy;
          }),
        });
      }

      groups[instr.action].push(instr);
    });

    if (paymentPlanTransactionKey === TRANSACTION_TYPE_VALUES.SWITCH_PLAN) {
      validatePortfolio(portfolio, [TRANSACTION_TYPE_VALUES.SWITCH_PLAN]);
    }

    return groups;
  }, [transactions, portfolio, paymentPlanTransactionKey, portfolio[`${paymentPlanTransactionKey}_errors`]]);
  // FIXME: now we still have some direct mutation in getValidPortfolios - so check for errors as workaround

  const onInstrumentsChange = (newInstruments) => {
    handlePortfolioChange(portfolio, {
      transactions: {
        ...portfolio.transactions,
        [paymentPlanTransactionKey]: newInstruments
      }
    });
  };

  const getComponents = (key) => {
    const data = (portfolio && portfolio.data) || {};

    const mainComponentsKey = data.hasOwnProperty("base_components")
      ? "base_components"
      : "components";

    const paymentPlansKey = data.hasOwnProperty(paymentPlanTransactionKey)
      ? paymentPlanTransactionKey
      : "components";

    let components = ["edit", "delete"].includes(key)
      ? (data[paymentPlansKey] || []).map((component) =>
          extendPaymentPlanWithInstrumentData(component, data[mainComponentsKey] || [])
        )
      : data[mainComponentsKey] || [];

    if (key === "create") {
      if (paymentPlanTransactionKey !== TRANSACTION_TYPE_VALUES.PAYOUT_PLAN) {
        components = _.concat(components, _.get(data, "soldAssets") || []);
      }
      return _.clone(getPortfolioComponentsWithSubDepots(components));
    }

    const componentKey = "payment_id";
    return components.filter(
      (i) => !transactions.map((t) => t.data[componentKey]).includes(i[componentKey])
    );
  };

  return {
    transactions,
    groupedTransactions,
    onInstrumentsChange,
    getComponents,
    updatePlanDefaults,
  };
}
